import { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft, Save, Eye, Globe, Upload, Plus, Trash2,
  GripVertical, Image, Video, Music, Calendar, Mail,
  User, Layout, Palette, Settings, ExternalLink, Newspaper, Copy, Quote, Star,
  Briefcase, Users, HelpCircle, MessageSquare, Phone, CheckCircle2, XCircle, AlertTriangle, Link, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import CloudinaryUploader, { GalleryUploader } from '@/components/CloudinaryUploader';

export const MusicModuleEditor = ({ data, onChange, siteSlug }) => {
  const tracks = data.tracks || [];
  const [editingIdx, setEditingIdx] = useState(null);
  const [soundcloudEmbed, setSoundcloudEmbed] = useState('');
  
  // Extract src URL from embed code if pasted
  const handleEmbedCodeChange = (value) => {
    // Check if it's a full iframe embed code
    const srcMatch = value.match(/src=["']([^"']+)["']/);
    if (srcMatch) {
      // Extract just the URL from the iframe
      onChange({...data, spotify_embed_code: value, spotify_embed_url: srcMatch[1]});
    } else if (value.includes('open.spotify.com')) {
      // It's just a URL, convert to embed URL if needed
      let embedUrl = value;
      if (!value.includes('/embed/')) {
        embedUrl = value.replace('open.spotify.com/', 'open.spotify.com/embed/');
      }
      onChange({...data, spotify_embed_code: '', spotify_embed_url: embedUrl});
    } else {
      onChange({...data, spotify_embed_code: value, spotify_embed_url: ''});
    }
  };

  // Handle SoundCloud embed code
  const handleSoundcloudEmbed = (value) => {
    const srcMatch = value.match(/src=["']([^"']+)["']/);
    if (srcMatch) {
      onChange({...data, soundcloud_embed_code: value, soundcloud_embed_url: srcMatch[1]});
    } else {
      onChange({...data, soundcloud_embed_code: value, soundcloud_embed_url: ''});
    }
  };

  const addTrack = () => {
    const newTrack = {
      id: Date.now().toString(),
      title: 'New Track',
      artist: data.default_artist || '',
      audio_file: '', // For MP3 uploads
      cover_image: '', // Album art
      spotify_url: '',
      apple_music_url: '',
      youtube_music_url: '',
      soundcloud_url: ''
    };
    onChange({...data, tracks: [...tracks, newTrack]});
    setEditingIdx(tracks.length);
  };

  const updateTrack = (idx, updates) => {
    const updated = [...tracks];
    updated[idx] = {...updated[idx], ...updates};
    onChange({...data, tracks: updated});
  };

  const removeTrack = (idx) => {
    onChange({...data, tracks: tracks.filter((_, i) => i !== idx)});
    setEditingIdx(null);
  };

  const [uploadingTrackIdx, setUploadingTrackIdx] = useState(null);

  // Audio file upload handler using signed Cloudinary upload
  const handleAudioUpload = async (idx, file) => {
    if (!file) return;
    setUploadingTrackIdx(idx);
    
    try {
      // Get signed upload params from backend (resource_type 'video' for audio)
      const folder = `sites/${siteSlug}/music`;
      const sigResponse = await api.get('/cloudinary/signature', {
        params: { folder, resource_type: 'video' }
      });
      const { signature, timestamp, cloud_name, api_key } = sigResponse.data;

      const formData = new FormData();
      formData.append('file', file);
      formData.append('signature', signature);
      formData.append('timestamp', timestamp);
      formData.append('api_key', api_key);
      formData.append('folder', folder);

      const response = await fetch(`https://api.cloudinary.com/v1_1/${cloud_name}/video/upload`, {
        method: 'POST',
        body: formData
      });
      const result = await response.json();
      if (result.secure_url) {
        updateTrack(idx, { audio_file: result.secure_url });
        toast.success('Audio file uploaded');
      } else {
        toast.error('Upload failed: ' + (result.error?.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Audio upload failed:', error);
      toast.error('Failed to upload audio file. Please try again.');
    } finally {
      setUploadingTrackIdx(null);
    }
  };

  const renderTrackEditor = (track, idx) => (
    <div className="bg-slate-800 border border-slate-600 rounded-lg p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-white">Edit Track</h4>
        <Button variant="ghost" size="sm" onClick={() => setEditingIdx(null)} className="text-slate-400 hover:text-white">Done</Button>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Track Title</Label>
          <Input value={track.title} onChange={(e) => updateTrack(idx, { title: e.target.value })} placeholder="Song title" />
        </div>
        <div className="space-y-2">
          <Label>Artist</Label>
          <Input value={track.artist || ''} onChange={(e) => updateTrack(idx, { artist: e.target.value })} placeholder="Artist name" />
        </div>
      </div>

      {/* MP3 Upload Section */}
      <div className="p-4 bg-violet-900/20 border border-violet-700/50 rounded-lg space-y-3">
        <Label className="flex items-center gap-2 text-violet-300 font-medium">
          <Music className="w-4 h-4" />
          Audio File (MP3)
        </Label>
        
        {track.audio_file ? (
          <div className="space-y-2">
            <audio controls className="w-full" src={track.audio_file}>
              Your browser does not support the audio element.
            </audio>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => updateTrack(idx, { audio_file: '' })}
              className="text-red-400 hover:text-red-300"
            >
              <Trash2 className="w-3 h-3 mr-1" /> Remove Audio
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {uploadingTrackIdx === idx ? (
              <div className="flex items-center gap-3 p-3 bg-violet-900/30 rounded-lg">
                <div className="w-5 h-5 border-2 border-violet-400 border-t-transparent rounded-full animate-spin" />
                <span className="text-sm text-violet-300">Uploading audio...</span>
              </div>
            ) : (
              <input
                type="file"
                accept="audio/mp3,audio/mpeg,audio/wav,audio/m4a"
                onChange={(e) => handleAudioUpload(idx, e.target.files?.[0])}
                className="block w-full text-sm text-slate-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-violet-600 file:text-white hover:file:bg-violet-500 file:cursor-pointer"
              />
            )}
            <p className="text-xs text-violet-300/70">Upload MP3, WAV, or M4A (max 10MB)</p>
          </div>
        )}
      </div>

      {/* Cover Art */}
      <div className="space-y-2">
        <Label>Cover Art (optional)</Label>
        <CloudinaryUploader
          currentImage={track.cover_image}
          onUpload={(url) => updateTrack(idx, { cover_image: url })}
          onRemove={() => updateTrack(idx, { cover_image: '' })}
          folder={`sites/${siteSlug}/music/covers`}
          aspectRatio="aspect-square"
          placeholder="Drop album artwork"
        />
      </div>

      <div className="space-y-4 p-4 bg-slate-700/30 rounded-lg">
        <p className="text-xs text-slate-400 font-medium uppercase tracking-wide">Streaming Links (optional - for external platforms)</p>
        
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-green-500"></span>
            Spotify
          </Label>
          <Input value={track.spotify_url || ''} onChange={(e) => updateTrack(idx, { spotify_url: e.target.value })} placeholder="https://open.spotify.com/track/..." />
        </div>
        
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-pink-500"></span>
            Apple Music
          </Label>
          <Input value={track.apple_music_url || ''} onChange={(e) => updateTrack(idx, { apple_music_url: e.target.value })} placeholder="https://music.apple.com/..." />
        </div>
        
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-red-500"></span>
            YouTube Music
          </Label>
          <Input value={track.youtube_music_url || ''} onChange={(e) => updateTrack(idx, { youtube_music_url: e.target.value })} placeholder="https://music.youtube.com/watch?v=..." />
        </div>
        
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-orange-500"></span>
            SoundCloud
          </Label>
          <Input value={track.soundcloud_url || ''} onChange={(e) => updateTrack(idx, { soundcloud_url: e.target.value })} placeholder="https://soundcloud.com/..." />
        </div>
      </div>

      <div className="pt-2 border-t border-slate-700">
        <Button variant="ghost" size="sm" onClick={() => removeTrack(idx)} className="text-red-400 hover:text-red-300 hover:bg-red-500/20">
          <Trash2 className="w-4 h-4 mr-2" /> Delete Track
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label>Section Title</Label>
        <Input 
          value={data.title || 'Music'} 
          onChange={(e) => onChange({...data, title: e.target.value})}
        />
      </div>

      {/* Main Spotify Embed - for featured album/playlist */}
      <div className="p-4 bg-emerald-900/20 border border-emerald-700/50 rounded-lg space-y-3">
        <div className="flex items-center gap-2">
          <Music className="w-5 h-5 text-emerald-400" />
          <Label className="text-emerald-300 font-medium">Featured Spotify Player</Label>
        </div>
        <Textarea 
          value={data.spotify_embed_code || data.spotify_embed_url || ''} 
          onChange={(e) => handleEmbedCodeChange(e.target.value)}
          placeholder='Paste Spotify embed code or URL for album, playlist, or artist'
          rows={3}
          className="font-mono text-xs"
        />
        <p className="text-xs text-emerald-300/70">
          Spotify → ••• → Share → Embed → Copy code
        </p>
        {data.spotify_embed_url && (
          <div className="rounded-xl overflow-hidden">
            <iframe
              src={data.spotify_embed_url}
              width="100%"
              height="152"
              frameBorder="0"
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="lazy"
            />
          </div>
        )}
      </div>

      {/* SoundCloud Embed */}
      <div className="p-4 bg-orange-900/20 border border-orange-700/50 rounded-lg space-y-3">
        <div className="flex items-center gap-2">
          <Music className="w-5 h-5 text-orange-400" />
          <Label className="text-orange-300 font-medium">Featured SoundCloud Player</Label>
        </div>
        <Textarea 
          value={data.soundcloud_embed_code || data.soundcloud_embed_url || ''} 
          onChange={(e) => handleSoundcloudEmbed(e.target.value)}
          placeholder='Paste SoundCloud embed code for a track, playlist, or profile'
          rows={3}
          className="font-mono text-xs"
        />
        <p className="text-xs text-orange-300/70">
          SoundCloud → Share → Embed → Copy code
        </p>
        {data.soundcloud_embed_url && (
          <div className="rounded-lg overflow-hidden">
            <iframe
              src={data.soundcloud_embed_url}
              width="100%"
              height="166"
              frameBorder="0"
              allow="autoplay"
              loading="lazy"
            />
          </div>
        )}
      </div>

      {/* Streaming Platform Links */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Spotify Artist Page</Label>
          <Input 
            value={data.spotify_artist_url || ''} 
            onChange={(e) => onChange({...data, spotify_artist_url: e.target.value})}
            placeholder="https://open.spotify.com/artist/..."
          />
        </div>
        <div className="space-y-2">
          <Label>Apple Music Artist Page</Label>
          <Input 
            value={data.apple_music_url || ''} 
            onChange={(e) => onChange({...data, apple_music_url: e.target.value})}
            placeholder="https://music.apple.com/..."
          />
        </div>
        <div className="space-y-2">
          <Label>YouTube Music Channel</Label>
          <Input 
            value={data.youtube_music_url || ''} 
            onChange={(e) => onChange({...data, youtube_music_url: e.target.value})}
            placeholder="https://music.youtube.com/channel/..."
          />
        </div>
        <div className="space-y-2">
          <Label>SoundCloud Profile</Label>
          <Input 
            value={data.soundcloud_url || ''} 
            onChange={(e) => onChange({...data, soundcloud_url: e.target.value})}
            placeholder="https://soundcloud.com/..."
          />
        </div>
      </div>

      {/* Individual Tracks */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label>Individual Tracks ({tracks.length})</Label>
          <Button variant="outline" size="sm" onClick={addTrack}>
            <Plus className="w-4 h-4 mr-1" /> Add Track
          </Button>
        </div>
        
        {tracks.length === 0 ? (
          <div className="text-center py-8 bg-slate-700/30 rounded-lg border border-slate-600">
            <p className="text-sm text-slate-400">Add individual tracks with links to multiple streaming platforms</p>
          </div>
        ) : editingIdx !== null && tracks[editingIdx] ? (
          renderTrackEditor(tracks[editingIdx], editingIdx)
        ) : (
          <div className="space-y-2">
            {tracks.map((track, idx) => (
              <div 
                key={track.id || idx} 
                onClick={() => setEditingIdx(idx)}
                className="flex items-center gap-4 p-3 bg-slate-700/30 border border-slate-600 rounded-lg cursor-pointer hover:bg-slate-700/50 hover:border-slate-500 transition-all group"
              >
                <div className="w-10 h-10 rounded bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center flex-shrink-0">
                  <Music className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-white truncate">{track.title || 'Untitled'}</h4>
                  <p className="text-xs text-slate-400">{track.artist || 'No artist'}</p>
                </div>
                <div className="flex gap-1">
                  {track.spotify_url && <span className="w-2 h-2 rounded-full bg-green-500" title="Spotify"></span>}
                  {track.apple_music_url && <span className="w-2 h-2 rounded-full bg-pink-500" title="Apple Music"></span>}
                  {track.youtube_music_url && <span className="w-2 h-2 rounded-full bg-red-500" title="YouTube"></span>}
                  {track.soundcloud_url && <span className="w-2 h-2 rounded-full bg-orange-500" title="SoundCloud"></span>}
                </div>
                <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); removeTrack(idx); }} className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 hover:bg-red-500/20 h-8 w-8 p-0">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Mailing List Module Editor

